/**
 * ShoppingList + ShoppingListItem.
 *
 * Ported from legacy `frontend/src/types.ts`. A ShoppingList is a household-owned
 * container; a ShoppingListItem must reference at least one of HouseholdProduct or
 * RetailerOffer (invariant I6), never Platform Seed Data directly (I1).
 */

import { z } from 'zod';
import {
  pgNumeric,
  priceCurrencySchema,
  shoppingListItemStatusSchema,
  shoppingListStatusSchema,
  shoppingListTypeSchema,
} from './primitives';

export const shoppingListSchema = z.object({
  id: z.string(),
  household_id: z.string(),
  name: z.string(),
  list_type: shoppingListTypeSchema,
  status: shoppingListStatusSchema,
  created_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  pending_count: z.number().optional(),
  total_count: z.number().optional(),
});
export type ShoppingList = z.infer<typeof shoppingListSchema>;

// T12.0.1.5 — Offer-only SLI path dropped (2026-05-05).
// T12.0.1.6 — selected_flyerdeal_id added; mutex with selected_retaileroffer_id.
// T12.0.2.1 — selected_flyerdeal_id removed (2026-05-06). The SLI references
// at most a RetailerOffer; deals surface at read time via
// `flyerdeals.matched_retaileroffer_id = sli.selected_retaileroffer_id` with a
// live-validity predicate. The mutex CHECK is gone (no longer modelled).
// `householdproduct_id` is required (NOT NULL in DB). The 4 offer snapshot fields
// were removed in T12.0.1.5.
//
// Note: The schema uses `.optional()` on `householdproduct_id` to preserve
// backwards-compat with existing client rows until the next server sync
// overwrites stale Dexie-cached items. TypeScript types (ShoppingListItem)
// reflect the authoritative shape (required). See T12.0.1.5 spec for details.
// Wire shape: backend serializers emit snake_case for shoppinglistitems
// columns (selected_retaileroffer_id, quantity_unit). T12.0.18.1.1 Step 2.5
// briefly collapsed within-chunk underscores on the client side; the Step 3
// follow-up reverted that collapse so the Zod schema mirrors the wire shape
// verbatim again. `shoppinglist_id`, `householdproduct_id`, `purchased_at`,
// `purchased_by`, `added_by` keep their standard suffixes.
export const shoppingListItemSchema = z.object({
  id: z.string(),
  shoppinglist_id: z.string(),

  // References — householdproduct_id required.
  householdproduct_id: z.string(),
  // T27.2.7.9.3 — the where-to-buy store anchor (the chosen StoreLocation).
  // Optional (NULL = no store chosen yet); FK ON DELETE SET NULL. The contract's
  // single where-to-buy anchor — `selected_retaileroffer_id` below is the
  // OPTIONAL carried deal, only ever set alongside a store and always at that
  // same store. `.optional()` so legacy Dexie-cached rows (pre-T27.2.7.9.3)
  // still parse.
  selected_storelocation_id: z.string().nullable().optional(),
  selected_retaileroffer_id: z.string().nullable().optional(),

  // T12.1.8.7 — openable "added-from" deal reference (ON DELETE SET NULL) + a
  // validity-only snapshot frozen at add time. While the deal lives the
  // household reopens the exact deal it added (including recently-expired in
  // the resolver's 7-day window); on the deal's purge the FK nulls and the
  // snapshot persists, so the item can still state the window. This is openable
  // provenance — NOT a price anchor and NOT under an offer/deal mutex. The
  // current price the list shows is derived live via the SystemProduct.
  // `.optional()` so legacy Dexie-cached rows (pre-T12.1.8.7) still parse.
  flyerdeal_id: z.string().nullable().optional(),
  flyerdeal_valid_from: z.string().nullable().optional(),
  flyerdeal_valid_to: z.string().nullable().optional(),

  // Item Details
  quantity: pgNumeric,
  quantity_unit: z.string().nullable().optional(),
  checked: z.boolean(),
  notes: z.string().nullable().optional(),

  // Status
  status: shoppingListItemStatusSchema,
  purchased_at: z.string().nullable().optional(),
  purchased_by: z.string().nullable().optional(),

  // Audit
  added_by: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ShoppingListItem = z.infer<typeof shoppingListItemSchema>;

// T12.1.8.4 — the "would create — confirm?" signal an offer-only add returns
// (200) when no HouseholdProduct is linked to the offer's SystemProduct line
// yet. The client shows a one-tap confirm and re-issues the add with
// `confirmCreate: true`. `needsConfirmation` is the discriminator.
export const addItemNeedsConfirmationSchema = z.object({
  needsConfirmation: z.literal(true),
  systemproductId: z.string(),
  systemProductName: z.string(),
  // T12.1.8.7 — echoed back so the client re-issues the SAME add with
  // `confirmCreate: true`. Exactly one is present: an offer-add echoes the offer,
  // a flyer-add echoes the deal (+ the client re-sends its chosen line).
  selectedRetailerOfferId: z.string().optional(),
  flyerDealId: z.string().optional(),
});
export type AddItemNeedsConfirmation = z.infer<typeof addItemNeedsConfirmationSchema>;

// The add-item response is either the created item or the confirm-create signal.
// `addToActive` / `addItem` validate against this union; callers discriminate on
// `'needsConfirmation' in result`.
export const addShoppingListItemResponseSchema = z.union([
  addItemNeedsConfirmationSchema,
  shoppingListItemSchema,
]);
export type AddShoppingListItemResponse = z.infer<typeof addShoppingListItemResponseSchema>;

export const shoppingListItemWithDetailsSchema = shoppingListItemSchema.extend({
  // From list
  list_name: z.string(),
  household_id: z.string(),

  // From HouseholdProduct (if set)
  product_name: z.string().nullable().optional(),
  category_name: z.string().nullable().optional(),
  product_default_quantity: pgNumeric.nullable().optional(),
  // T36.3 / ADR-015 §6 — the HouseholdProduct's resolved measurement snapshot,
  // JOINed onto the SLI so the row renders `4 L` / `12 × 20 g` through the ONE
  // shared formatter. NULL when the product has no resolved measurement.
  product_measurement_pack_count: z.number().int().nullable().optional(),
  product_measurement_value: pgNumeric.nullable().optional(),
  product_measurement_unit: z.string().nullable().optional(),
  product_measurement_dimension: z.string().nullable().optional(),
  product_measurement_forms: z.record(z.string(), z.number()).nullable().optional(),
  product_expected_price: pgNumeric.nullable().optional(),
  product_expected_price_currency: priceCurrencySchema.nullable().optional(),
  product_expected_price_notes: z.string().nullable().optional(),
  product_notes: z.string().nullable().optional(),

  // T12.0.2.1 — From the SLI's RetailerOffer (catalog, durable anchor). Deal-side
  // fields (`current_price`, `deal_description`, `deal_end_date`,
  // `is_flyer_item`) are read-time-derived: a server-side live-deal lateral
  // subquery (D1 + D1a) joins `flyerdeals.matched_retaileroffer_id =
  // selected_retaileroffer_id` with the validity predicate
  // `valid_from <= CURRENT_DATE AND valid_to >= CURRENT_DATE` and picks the
  // winning row by lowest `sale_price`. When no live deal applies, deal-side
  // fields are NULL and `is_flyer_item` is false.
  offer_title: z.string().nullable().optional(),       // retaileroffers.title
  offer_brand: z.string().nullable().optional(),       // retaileroffers.brand
  current_price: pgNumeric.nullable().optional(),      // live FlyerDeal sale_price (or NULL)
  regular_price: pgNumeric.nullable().optional(),      // retaileroffers.regular_price (catalog only)
  price_currency: priceCurrencySchema.nullable().optional(),
  store_id: z.string().nullable().optional(),
  store_name: z.string().nullable().optional(),
  deal_description: z.string().nullable().optional(),  // live FlyerDeal sale_description
  deal_end_date: z.string().nullable().optional(),     // live FlyerDeal valid_to
  is_flyer_item: z.boolean().nullable().optional(),    // live FlyerDeal exists for this SLI's RetailerOffer

  // T27.2.7.9.5 — per-store live Flyer deals for the item's SystemProduct(s),
  // one entry per SELECTED store that has a live deal. Resolved by SystemProduct +
  // retailer/region via `flyerDealsResolver` (the same resolver pass the headline
  // `display_price` uses), so it is DECOUPLED from the sparse catalog
  // `retaileroffers` table: a store shows a deal price here whenever a flyer deal
  // is live at its retailer/region, whether or not a catalog offer row exists.
  // The Where to buy picker keys this by `storelocation_id` to render each store's
  // deals. T27.2.7.9.6 — carries the FULL set of matched deals per store (one
  // entry per deal, deduped by `flyerdeal_id`), each with identity so the picker
  // can list a store's deals by flyer title and mark which one is pinned (the
  // entry whose `flyerdeal_id` equals the item's). Omitted/empty when the item has
  // no live deal at any selected store. `.optional()` so legacy Dexie-cached rows
  // still parse.
  store_deals: z
    .array(
      z.object({
        storelocation_id: z.string(),
        // T27.2.7.9.6 — the deal's own identity. `flyerdeal_id` lets the client
        // mark the pinned entry; `title`/`brand` label it in the browse list.
        flyerdeal_id: z.string(),
        title: z.string(),
        brand: z.string().nullable(),
        sale_price: pgNumeric,
        // T27.2.7.9.5 (active-vs-upcoming) — `status` distinguishes a deal you
        // can get today ('active', shown as "ends <date>") from a not-yet-started
        // one ('upcoming', shown as "starts <date>", never a current price).
        valid_from: z.string(),
        valid_to: z.string(),
        status: z.union([z.literal('active'), z.literal('upcoming')]),
      }),
    )
    .optional(),

  // Computed display fields (use snapshot as fallback)
  display_title: z.string(),
  display_price: pgNumeric.nullable().optional(),
  display_store: z.string().nullable().optional(),

  // Audit
  added_by_name: z.string().nullable().optional(),
  purchased_by_name: z.string().nullable().optional(),
});
export type ShoppingListItemWithDetails = z.infer<typeof shoppingListItemWithDetailsSchema>;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export const shoppingListsResponseSchema = z.object({
  lists: z.array(shoppingListSchema),
});
export type ShoppingListsResponse = z.infer<typeof shoppingListsResponseSchema>;

export const shoppingListWithItemsResponseSchema = z.object({
  list: shoppingListSchema,
  items: z.array(shoppingListItemWithDetailsSchema),
});
export type ShoppingListWithItemsResponse = z.infer<
  typeof shoppingListWithItemsResponseSchema
>;

// `list` is `nullable()` because a household could (in pathological cases)
// have no ACTIVE list. The DB onboarding path creates one for every household,
// and the unique partial index guarantees ≤ 1 ACTIVE list per household, so
// the field is essentially always populated in practice — but the type stays
// honest. Frontend reads `list?.id` to drive add-item flows; this fixed a bug
// where empty `items[]` made the list id unreachable.
export const activeShoppingListResponseSchema = z.object({
  list: shoppingListSchema.nullable(),
  items: z.array(shoppingListItemWithDetailsSchema),
  total: z.number(),
});
export type ActiveShoppingListResponse = z.infer<typeof activeShoppingListResponseSchema>;

export const batchPurchaseResponseSchema = z.object({
  updated: z.number(),
  items: z.array(shoppingListItemWithDetailsSchema),
});
export type BatchPurchaseResponse = z.infer<typeof batchPurchaseResponseSchema>;
